import { useState } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

const PROVIDERS = ['Claude', 'ChatGPT', 'Deepseek', 'Gemini', 'Mock']
const DEFAULT_MODELS: Record<string, string> = {
    Claude: 'claude-sonnet-4-6',
    ChatGPT: 'gpt-4o-mini',
    Deepseek: 'deepseek-chat',
    Gemini: 'gemini-2.0-flash',
    Mock: 'mock'
}

export default function AdminAIConfigPage() {
    const { data, mutate } = useSWR<any>('/api/ai-providers', fetcher)
    const { data: active } = useSWR<any>('/api/ai-providers/active', fetcher)

    const [provider, setProvider] = useState('Claude')
    const [model, setModel] = useState(DEFAULT_MODELS.Claude)
    const [apiKey, setApiKey] = useState('')
    const [rankingTemp, setRankingTemp] = useState(0.2)
    const [emailTemp, setEmailTemp] = useState(0.7)
    const [maxTokens, setMaxTokens] = useState(4096)
    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)

    function pickProvider(p: string) {
        setProvider(p)
        setModel(DEFAULT_MODELS[p] ?? '')
    }

    async function activate() {
        setBusy(true); setMsg(null)
        try {
            await api('/api/ai-providers/active', {
                method: 'POST',
                body: JSON.stringify({
                    provider,
                    model,
                    apiKey: apiKey || null,
                    rankingTemperature: rankingTemp,
                    emailTemperature: emailTemp,
                    maxTokens
                })
            })
            setMsg(`${provider} is now active.`)
            setApiKey('')
            mutate()
        } catch (e: any) { setMsg(`Error: ${e.message}`) }
        finally { setBusy(false) }
    }

    return (
        <div>
            <h1>AI Provider Configuration</h1>
            <p className="muted">Pick the active provider, set its API key, tune temperatures. Only admins see this page.</p>

            <div className="card">
                <h2>Active Provider</h2>
                {active?.active === false ? <p className="muted">No provider configured. Defaulting to Mock for ranking calls.</p> :
                    active && <p>
                        <strong>{active.providerName}</strong> · {active.modelName} · ranking temp {active.rankingTemperature} · email temp {active.emailTemperature} · max tokens {active.maxTokens}
                    </p>
                }
            </div>

            <div className="card">
                <h2>Set Active Provider</h2>
                <div className="split">
                    <div className="form-row">
                        <label>Provider</label>
                        <select value={provider} onChange={e => pickProvider(e.target.value)}>
                            {PROVIDERS.map(p => <option key={p}>{p}</option>)}
                        </select>
                    </div>
                    <div className="form-row">
                        <label>Model</label>
                        <input value={model} onChange={e => setModel(e.target.value)} />
                    </div>
                    <div className="form-row">
                        <label>API Key (leave blank to keep existing)</label>
                        <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-..." />
                    </div>
                    <div className="form-row">
                        <label>Max Tokens</label>
                        <input type="number" value={maxTokens} onChange={e => setMaxTokens(parseInt(e.target.value) || 4096)} />
                    </div>
                    <div className="form-row">
                        <label>Ranking Temperature ({rankingTemp})</label>
                        <input type="range" min="0" max="1" step="0.05" value={rankingTemp} onChange={e => setRankingTemp(parseFloat(e.target.value))} />
                    </div>
                    <div className="form-row">
                        <label>Email Temperature ({emailTemp})</label>
                        <input type="range" min="0" max="1" step="0.05" value={emailTemp} onChange={e => setEmailTemp(parseFloat(e.target.value))} />
                    </div>
                </div>
                {msg && <div className={msg.startsWith('Error') ? 'error' : 'success'}>{msg}</div>}
                <button onClick={activate} disabled={busy} data-testid="activate-provider">Activate {provider}</button>
            </div>

            <div className="card">
                <h2>All Configured Providers</h2>
                {!data?.length ? <p className="muted">None configured yet.</p> :
                    <table>
                        <thead><tr><th>Provider</th><th>Model</th><th>Ranking T</th><th>Email T</th><th>Max Tokens</th><th>Active</th></tr></thead>
                        <tbody>
                            {data.map((c: any) => (
                                <tr key={c.providerName}>
                                    <td>{c.providerName}</td>
                                    <td>{c.modelName}</td>
                                    <td>{c.rankingTemperature}</td>
                                    <td>{c.emailTemperature}</td>
                                    <td>{c.maxTokens}</td>
                                    <td>{c.isActive ? '✓' : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                }
            </div>
        </div>
    )
}
